import { differenceInSeconds, parseISO } from 'date-fns'
import { useFormikContext } from 'formik'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  getNumberOfUnitsInLot,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  useAnalyticsContext,
} from '@b-stock/fe-core/bstock-react'
import type { PayMethod } from '@b-stock/payments-methods-api-client'

import type { BidFormFormikValues } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import { useProblemFreeOrderRateAnalytics } from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import { derivePaymentMode } from '@/buyer-portal/components/features/order/checkout/helpers/utils'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { buildGeofenceBidAnalyticsProperties } from '@/buyer-portal/helpers/shipping'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { LANDED_COST_SEARCH_FILTER_SESSION_KEY } from '@/components/listings/search/SearchFilters/filters/LandedCostPercentageFilter/LandedCostPercentageFilter'
import { attAccessRouteProperty } from '@/components/storefrontAccess/attAccessRoute'
import { useAttAccessRoute } from '@/components/storefrontAccess/useAttAccessRoute'
import { FeatureFlags } from '@/helpers/featureFlags'
import { toFirstDibsSegmentProps } from '@/helpers/telemetry/firstDibsSegmentProps'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'
import { watchedStatusQuery } from '@/queries/watchlistQuery'

export const useTrackBidConfirm = (
  listing: OrchestratedAuctionListingDetails,
  /**
   * Whether Freeze Sort (GLOB-4832) is active on the List View this bid
   * originated from, so bids can be segmented by frozen vs. unfrozen state.
   * Only ever supplied by the List View inline-bid path (AuctionActionCell) —
   * omitted (rather than false) for LDP bids, where the concept doesn't
   * apply, so the property is left off those events entirely instead of
   * misleadingly reporting `false`.
   */
  isSortFrozen?: boolean,
  /**
   * Freeze Sort session analytics (GLOB-4832/BP-2422): call when a bid is
   * confirmed while `isSortFrozen` is true, so `freeze_sort_session`'s
   * `bids_placed_while_frozen` reflects it. Threaded the same way as
   * `isSortFrozen` — only supplied by the List View path.
   */
  recordBidPlacedWhileFrozen?: () => void
) => {
  const { trackButtonClicked, track } = useAnalytics()
  const { screenName } = useAnalyticsContext()
  const { values } = useFormikContext<BidFormFormikValues>()
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const { activeGroup: fdActiveGroup } = useFirstDibsPurchase(listing)
  const isTlcDisplayed =
    !!listing.lot.retailValue || (getNumberOfUnitsInLot(listing.lot) ?? 0) > 0
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })

  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId
  const { data: watchedData, isLoading: isWatchedLoading } =
    watchedStatusQuery.useWithOpts([{ personId, listingId: listing._id }], {
      enabled: !!personId,
    })

  // Fetch P2S analytics data
  const p2sData = useListingP2SData(listing.lot.locationOriginalId)

  const problemFreeOrderRateAnalytics =
    useProblemFreeOrderRateAnalytics(listing)

  // AT&T only: the buyer's access route; undefined (no property) elsewhere.
  const { attAccessRoute } = useAttAccessRoute(listing)

  const trackBidConfirm = ({
    bidAmount,
    selectedQuote,
    selectedPaymentMethod,
    isWireSelected,
    totalLandedCostWithTax,
    anyAddressWithinRadius,
    currentBidUsd,
    minimumRequiredBidUsd,
    bstockFeeUsd,
  }: {
    bidAmount: number
    selectedQuote?: OrchestratedQuote | null
    selectedPaymentMethod?: PayMethod | null
    isWireSelected?: boolean
    /**
     * Tax-inclusive, so the event reports the total the buyer confirmed
     * (FP-2498). Only the modal knows max tax exposure — the bid form
     * provider's `details.totalLandedCost` is pre-tax.
     */
    totalLandedCostWithTax: number
    anyAddressWithinRadius: boolean
    currentBidUsd: number | null
    minimumRequiredBidUsd: number | null
    bstockFeeUsd: number | null
  }) => {
    const { actualEndTime, numberOfBids } = listing.auction

    const timeDiffInSeconds = differenceInSeconds(
      parseISO(actualEndTime || new Date().toDateString()),
      new Date()
    )

    const paymentMode = derivePaymentMode(
      selectedPaymentMethod?.type,
      isWireSelected
    )

    trackButtonClicked({
      buttonName: 'confirm_max_bid',
      screenName,
      time_left: timeDiffInSeconds,
      bid_amt_usd: bidAmount,
      is_new_seller:
        isNewSellerTreatment &&
        (newSellerIds?.has(listing.storefrontId) ?? false),
      total_landed_cost_usd: totalLandedCostWithTax,
      current_bid_usd: currentBidUsd,
      minimum_required_bid_usd: minimumRequiredBidUsd,
      bstock_fee_usd: bstockFeeUsd,
      bids_existing: numberOfBids,
      autocharge_listing: !!listing.autoPay,
      is_new_bid_box_design: true,
      is_total_landed_cost_displayed: isTlcDisplayed,
      ...(paymentMode && { payment_mode: paymentMode }),
      ...(selectedQuote?.carrier?.name && {
        carrier: selectedQuote.carrier.name,
      }),
      ...(values.bidType && { bid_type: values.bidType }),
      ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
      ...problemFreeOrderRateAnalytics,
      ...(!isWatchedLoading && { has_listing_note: !!watchedData?.note }),
      ...(isSortFrozen !== undefined && { sort_frozen: isSortFrozen }),
      ...attAccessRouteProperty(attAccessRoute),
      ...buildGeofenceBidAnalyticsProperties({
        selectedQuote,
        anyAddressWithinRadius,
      }),
      ...toFirstDibsSegmentProps(fdActiveGroup, { withEarlyPrice: true }),
    })

    const landedCostSession = sessionStorage.getItem(
      LANDED_COST_SEARCH_FILTER_SESSION_KEY
    )
    if (landedCostSession) {
      try {
        const { value: filterValue } = JSON.parse(landedCostSession) as {
          value: number
        }
        track('landed_cost_bid_confirmed', {
          bid_amt_usd: bidAmount,
          landed_cost_filter_value: Math.round(filterValue * 100),
          source: AnalyticsSource.HOME_PORTAL,
          entityType: AnalyticsEntityType.BUYER,
          screenName,
          ...(isSortFrozen !== undefined && { sort_frozen: isSortFrozen }),
        })
      } catch {
        // malformed sessionStorage entry — skip
      }
    }

    if (isSortFrozen) {
      recordBidPlacedWhileFrozen?.()
    }
  }

  return trackBidConfirm
}
